<script setup lang="ts">
import { Button } from '~/components/ui/button'
import { LucideEdit2, LucideUser, LucidePhone, LucideMail } from 'lucide-vue-next'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip'

const { t } = useI18n()
const { getPathWithLocale } = useLocalizedPath()
const { user, userName, userInitials } = useUser()
const router = useRouter()

// Format phone number to always show with +
const formattedPhone = computed(() => {
  if (!user.value?.phone) return t('feature.profile.not_specified')

  const phone = user.value.phone.toString().trim()

  // If phone already starts with +, return as is
  if (phone.startsWith('+')) return phone

  // Clean phone number (remove all non-digits)
  const cleanPhone = phone.replace(/\D/g, '')

  // If phone starts with 996, add +
  if (cleanPhone.startsWith('996')) return `+${cleanPhone}`

  // If phone doesn't start with 996, assume it's local Kyrgyz number and add +996
  if (cleanPhone.length >= 9 && !cleanPhone.startsWith('996')) {
    return `+996${cleanPhone}`
  }

  // For other cases, add + if it's just digits
  if (/^\d+$/.test(cleanPhone)) {
    return `+${cleanPhone}`
  }

  // Otherwise return as is
  return phone
})

const startEdit = () => {
  router.push(getPathWithLocale('/panel/profile/edit'))
}
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div
      class="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0"
    >
      <div>
        <h2 class="text-lg font-semibold text-gray-900">
          {{ $t('feature.profile.personal_info') }}
        </h2>
      </div>
      <Button class="w-full sm:w-auto" @click="startEdit">
        <LucideEdit2 class="h-4 w-4" />
        {{ $t('feature.profile.edit_button') }}
      </Button>
    </div>

    <!-- Profile Info -->
    <div>
      <!-- Avatar -->
      <div
        class="mb-8 flex flex-col items-center space-y-4 sm:flex-row sm:items-center sm:space-x-6 sm:space-y-0"
      >
        <div
          class="relative h-20 w-20 overflow-hidden rounded-[16px] bg-gradient-to-br from-blue-600 to-blue-600 shadow-lg sm:h-24 sm:w-24"
        >
          <NuxtImg
            v-if="user?.avatar"
            :src="user.avatar"
            :alt="userName"
            class="h-full w-full object-cover"
            loading="lazy"
            width="96"
            height="96"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center text-xl font-bold text-white sm:text-2xl"
          >
            {{ userInitials }}
          </div>
        </div>
        <div class="text-center sm:text-left">
          <h3 class="text-lg font-semibold text-gray-900 sm:text-xl">{{ userName }}</h3>
          <p
            class="flex items-center justify-center text-sm text-gray-500 sm:justify-start sm:text-base"
          >
            {{ user?.email }}
          </p>
        </div>
      </div>

      <!-- Info Grid -->
      <div class="grid gap-6 sm:gap-8 md:grid-cols-2">
        <div class="space-y-6">
          <div>
            <label class="flex items-center text-sm font-semibold">
              <LucideUser class="mr-2 h-4 w-4" />
              {{ $t('common.first_name') }}
            </label>
            <p class="mt-2 rounded-lg bg-gray-50 p-3 text-gray-900">
              {{ user?.first_name || $t('feature.profile.not_specified') }}
            </p>
          </div>

          <div>
            <label class="flex items-center text-sm font-semibold">
              <LucideUser class="mr-2 h-4 w-4" />
              {{ $t('common.last_name') }}
            </label>
            <p class="mt-2 rounded-lg bg-gray-50 p-3 text-gray-900">
              {{ user?.last_name || $t('feature.profile.not_specified') }}
            </p>
          </div>

          <div>
            <label class="flex items-center text-sm font-semibold">
              <LucideUser class="mr-2 h-4 w-4" />
              {{ $t('common.middle_name') }}
            </label>
            <p class="mt-2 rounded-lg bg-gray-50 p-3 text-gray-900">
              {{ user?.middle_name || $t('feature.profile.not_specified') }}
            </p>
          </div>
        </div>

        <div class="space-y-6">
          <div>
            <label class="flex items-center text-sm font-semibold">
              <LucideMail class="mr-2 h-4 w-4" />
              {{ $t('common.email') }}
            </label>
            <p class="mt-2 rounded-lg bg-gray-50 p-3 text-gray-900">
              {{ user?.email || $t('feature.profile.not_specified') }}
            </p>
          </div>

          <div>
            <label class="flex items-center text-sm font-semibold">
              <LucidePhone class="mr-2 h-4 w-4" />
              {{ $t('common.phone') }}
              <span
                v-if="user?.phone && !user?.is_phone_verified"
                class="ml-2 text-xs font-normal text-red-600"
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <span class="cursor-help">{{
                        $t('feature.profile.phone_not_verified')
                      }}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <div>{{ $t('feature.profile.phone_verification_tooltip') }}</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
            </label>
            <p class="mt-2 rounded-lg bg-gray-50 p-3 text-gray-900">
              {{ formattedPhone }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
